import React, { useState, useEffect } from 'react';
import Breadcrumbs from '../components/Breadcrumbs';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';

const BANNER_URL = 'https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg';

const ChangelogPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState('');
  const [changelog, setChangelog] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('jwt_token');
    if (storedToken) {
      setAuthToken(storedToken);
      setIsAuthenticated(true);
    }
  }, []); // Run once on mount

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchChangelog = async () => {
      try {
        const res = await fetch(apiPrefix + '/api/changelog', {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });
        if (!res.ok) throw new Error('Failed to fetch changelog');
        const data = await res.json();
        setChangelog(data);
      } catch (err) {
        setError('Error fetching changelog. Please log in.');
      } finally {
        setLoading(false);
      }
    };
    fetchChangelog();
  }, [isAuthenticated, authToken]);

  const handleAcknowledge = async () => {
    try {
      const res = await fetch(apiPrefix + '/api/changelog/acknowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (changelog) {
          localStorage.setItem('lastSeenChangelogVersion', changelog.version);
        }
        let countdown = 1;
        setError(`Acknowledged! Redirecting in ${countdown} seconds...`);
        const interval = setInterval(() => {
          countdown -= 1;
          setError(`Acknowledged! Redirecting in ${countdown} seconds...`);
          if (countdown === 0) {
        clearInterval(interval);
        window.location.href = '/';
          }
        }, 1000);
      } else {
        setError('Failed to acknowledge changelog. Please try again.');
      }
    } catch (err) {
      setError('Error acknowledging changelog. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 bg-light">
        <header className="bg-white shadow-sm">
          <div className="container-fluid p-0">
            <img
              src={BANNER_URL}
              alt="Institute for Intelligent Systems Banner"
              className="img-fluid w-100"
              style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }}
            />
          </div>
        </header>
        <div className="container py-4 flex-grow-1">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Changelog' }]} />
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card shadow-lg p-4 p-md-5" style={{ maxWidth: '500px', width: '100%' }}>
                <h2 className="card-title text-center mb-3">IIS Overleaf Proxy</h2>
                <p className="text-center mb-4">The system has changed. Please log in to view the changelog. You must acknowledge the changelog before proceeding to Overleaf.</p>
                {!isAuthenticated && (
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setLoginLoading(true);
                      setError('');
                      try {
                        const response = await fetch(apiPrefix + '/api/user-login', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email, password }),
                        });
                        const data = await response.json();
                        if (!response.ok) throw new Error(data.error || 'Login failed');
                        localStorage.setItem('jwt_token', data.token); // Store token
                        setAuthToken(data.token);
                        setIsAuthenticated(true);
                      } catch (err) {
                        setError(err.message);
                      } finally {
                        setLoginLoading(false);
                      }
                    }}>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                      <button type="submit" className="btn btn-primary w-100" disabled={loginLoading}>
                        {loginLoading ? 'Logging in...' : 'Login'}
                      </button>
                      {error && <div className="alert alert-danger mt-3">{error}</div>}
                    </form>
                )}
                {isAuthenticated && !changelog && (
                     <div className="text-center">
                        <p>Loading changelog...</p>
                     </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!changelog) {
    return (
      <div className="d-flex flex-column min-vh-100 bg-light">
        <header className="bg-white shadow-sm">
          <div className="container-fluid p-0">
            <img
              src={BANNER_URL}
              alt="Institute for Intelligent Systems Banner"
              className="img-fluid w-100"
              style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }}
            />
          </div>
        </header>
        <div className="container py-4 flex-grow-1">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Changelog' }]} />
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card shadow-lg p-4 p-md-5" style={{ maxWidth: '600px', width: '100%' }}>
                <div className="alert alert-warning">
                  <h4>Unable to Load Changelog</h4>
                  <p>We're having trouble loading the changelog. Please log in if you haven't already, or try again later.</p>
                  <button className="btn btn-primary" onClick={() => window.location.reload()}>
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <header className="bg-white shadow-sm">
        <div className="container-fluid p-0">
          <img
            src={BANNER_URL}
            alt="Institute for Intelligent Systems Banner"
            className="img-fluid w-100"
            style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }}
          />
        </div>
      </header>

      <div className="container py-4 flex-grow-1">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-lg p-4 p-md-5" style={{ maxWidth: '800px', width: '100%' }}>
              <div className="card-header bg-primary text-white">
                <h2 className="card-title mb-0 text-center">Changelog - Version {changelog.version}</h2>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <small className="text-muted">
                    <strong>Release Date:</strong> {new Date(changelog.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </small>
                </div>

                <h5 className="mb-3">What's New:</h5>
                <div className="changelog-content">
                  {changelog.changes && changelog.changes.length > 0 ? (
                    <ul className="list-group list-group-flush">
                      {changelog.changes.map((change, index) => (
                        <li key={index} className="list-group-item d-flex align-items-start">
                          <span className="badge bg-primary me-3 mt-1">•</span>
                          <span>{change}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">No changes listed for this version.</p>
                  )}
                </div>

                {changelog.notes && (
                  <div className="mt-4">
                    <h6>Additional Notes:</h6>
                    <div className="alert alert-info">
                      {changelog.notes}
                    </div>
                  </div>
                )}
              </div>
              <div className="card-footer text-center">
                <button
                  className="btn btn-success btn-lg px-4"
                  onClick={handleAcknowledge}
                >
                  I Acknowledge - Proceed to Overleaf
                </button>
                <p className="text-muted mt-2 mb-0 small">
                  You must acknowledge this changelog before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white border-top shadow-sm mt-auto py-3">
        <div className="container text-center">
          <a href="../portal-admin" className="btn btn-outline-primary me-2">
            Admin Portal
          </a>
          <a href="../install-packages" className="btn btn-outline-secondary">
            Install Packages
          </a>
        </div>
      </footer>
    </div>
  );
};

export default ChangelogPage;
